export type DeviceTier =
  | 'cast_dongle_high_risk'
  | 'cast_dongle_medium_risk'
  | 'cast_tv_high_risk'
  | 'cast_tv_low_risk'
  | 'android_tv'
  | 'smart_display'
  | 'unknown';

import type { system } from 'chromecast-caf-receiver/cast.framework';

type SDKCapability = `${system.DeviceCapabilities}`;

// satisfies ensures these values are typo-free against the SDK enum
const sdkCapabilities = {
  BLUETOOTH_SUPPORTED: 'bluetooth_supported',
  DISPLAY_SUPPORTED: 'display_supported',
  REMOTE_CONTROL_OVERLAY_SUPPORTED: 'remote_control_overlay_supported',
  SLEEP_TIMER_OVERLAY_SUPPORTED: 'sleep_timer_overlay_supported',
  TOUCH_INPUT_SUPPORTED: 'touch_input_supported',
  IS_HDR_SUPPORTED: 'is_hdr_supported',
  IS_DV_SUPPORTED: 'is_dv_supported',
  IS_DOLBY_ATMOS_SUPPORTED: 'is_dolby_atmos_supported',
  IS_CBCS_SUPPORTED: 'is_cbcs_supported',
} as const satisfies Record<string, SDKCapability>;

// Not present in cast.framework.system.DeviceCapabilities
const nonSdkCapabilities = {
  FOCUS_STATE_SUPPORTED: 'focus_state_supported',
  REMOTE_CONTROL_INPUT_SUPPORTED: 'remote_control_input_supported',
  WAKE_ON_LAN_SUPPORTED: 'wake_on_lan_supported',
  DPAD_INPUT_SUPPORTED: 'dpad_input_supported',
} as const;

export const DeviceCapability = { ...sdkCapabilities, ...nonSdkCapabilities } as const;

type DeviceCapability = (typeof DeviceCapability)[keyof typeof DeviceCapability];

export function getDeviceCapabilities(): DeviceCapability[] {
  const context = cast.framework.CastReceiverContext.getInstance();
  const caps: Record<string, boolean> = context.getDeviceCapabilities() ?? {};

  return Object.keys(caps).filter((key) => caps[key] === true) as DeviceCapability[];
}

export function getDeviceTier(): DeviceTier {
  const capabilityList = getDeviceCapabilities();

  const ua = navigator.userAgent ?? '';

  const isAndroidTV = ua.includes('DeviceType/AndroidTV');
  const isGoogleTV = ua.includes('Google TV');
  const isChromecast = ua.includes('CrKey') || ua.includes('Chromecast');
  const isFuchsia = ua.includes('Fuchsia');
  const isArmV7 = ua.includes('armv7l');

  const hasRemoteInput =
    capabilityList.includes(DeviceCapability.REMOTE_CONTROL_INPUT_SUPPORTED) ||
    capabilityList.includes(DeviceCapability.DPAD_INPUT_SUPPORTED);

  const hasAdvancedVideo =
    capabilityList.includes(DeviceCapability.IS_DV_SUPPORTED) ||
    capabilityList.includes(DeviceCapability.IS_DOLBY_ATMOS_SUPPORTED);

  // Nest Hub / Smart Displays
  if (capabilityList.includes(DeviceCapability.TOUCH_INPUT_SUPPORTED) || isFuchsia) {
    return 'smart_display';
  }

  // Google TV / Android TV
  if (isAndroidTV || isGoogleTV) {
    return 'android_tv';
  }

  if (isChromecast) {
    // Chromecast built-in TVs / embedded Cast platforms
    if (hasRemoteInput) {
      return hasAdvancedVideo ? 'cast_tv_low_risk' : 'cast_tv_high_risk';
    }

    // Older Chromecast dongles
    if (isArmV7 && !hasAdvancedVideo) {
      return 'cast_dongle_high_risk';
    }

    // Chromecast Ultra / newer dongles
    return 'cast_dongle_medium_risk';
  }

  return 'unknown';
}
